import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Layout from "./components/Layout";
import GoogleAnalytics from "./components/GoogleAnalytics";
import CookieConsent from "./components/CookieConsent";

// Eager-loaded (highest traffic landing pages : Home, City, Service).
// These represent 90 %+ of user entries from Google — pre-hydration blocking matters.
import HomePage from "./pages/HomePage";
import ServicePage from "./pages/ServicePage";
import CityServicePage, { allCityServicePaths } from "./pages/CityServicePage";
import CityHubPage, { allCityHubPaths } from "./pages/CityHubPage";
import ReparationParisPage, { REPARATION_PARIS_PATHS } from "./pages/ReparationParisPage";
import CityAliasRedirect, { allCityAliasPaths } from "./pages/CityAliasRedirect";
import PanneGeoPage, { allPanneGeoPaths } from "./pages/PanneGeoPage";
import { BRANDS } from "./data/brands";
import { allBrandAppliancePaths } from "./data/brand-appliance";

// Lazy-loaded (React 19 hydrateRoot keeps the pre-rendered HTML visible while the
// chunk loads — no flash). Keeps blog-content.js + error-codes.js out of main.js.
// react-snap correctly pre-renders these routes by resolving lazy imports during SSG.
const ParisHubPage = lazy(() => import("./pages/ParisHubPage"));
const ParisUrgentPage = lazy(() => import("./pages/ParisUrgentPage"));
const HautsDeSeinePage = lazy(() => import("./pages/HautsDeSeinePage"));
const YvelinesPage = lazy(() => import("./pages/YvelinesPage"));
const ValDeMarnePage = lazy(() => import("./pages/ValDeMarnePage"));
const PannePage = lazy(() => import("./pages/PannePage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const ErrorCodeHubPage = lazy(() => import("./pages/ErrorCodeHubPage"));
const ErrorCodeAppliancePage = lazy(() => import("./pages/ErrorCodeAppliancePage"));
const ErrorCodePage = lazy(() => import("./pages/ErrorCodePage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const BrandAppliancePage = lazy(() => import("./pages/BrandAppliancePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminSeoPage = lazy(() => import("./pages/AdminSeoPage"));
const ParisComparePage = lazy(() => import("./pages/ParisComparePage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const MethodologyPage = lazy(() => import("./pages/MethodologyPage"));
const ZonesPage = lazy(() => import("./pages/ZonesPage"));
const TarifsPage = lazy(() => import("./pages/TarifsPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const ReparerOuRacheterPage = lazy(() => import("./pages/ReparerOuRacheterPage"));
const EtudePannes2026Page = lazy(() => import("./pages/EtudePannes2026Page"));
const CarteInterventionsPage = lazy(() => import("./pages/CarteInterventionsPage"));

// Empty fallback : the pre-rendered HTML already shows content immediately.
// This is only visible during client-side navigation while chunk downloads.
const LazyFallback = () => null;

function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <GoogleAnalytics />
                <Layout>
                    <Suspense fallback={<LazyFallback />}>
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            {allBrandAppliancePaths().map((p) => (
                                <Route key={p} path={p} element={<BrandAppliancePage />} />
                            ))}
                            <Route path="/reparateur-lave-linge-paris" element={<ServicePage />} />
                            <Route path="/reparateur-lave-vaisselle-paris" element={<ServicePage />} />
                            <Route path="/reparateur-frigo-paris" element={<ServicePage />} />
                            <Route path="/reparation-four-paris" element={<ServicePage />} />
                            <Route path="/reparation-seche-linge-paris" element={<ServicePage />} />
                            {BRANDS.map((b) => (
                                <Route key={b.slug} path={`/reparateur-${b.slug}-paris`} element={<BrandPage />} />
                            ))}
                            {allCityServicePaths().map((p) => (
                                <Route key={p} path={p} element={<CityServicePage />} />
                            ))}
                            <Route path="/reparateur-electromenager-paris" element={<ParisHubPage />} />
                            {allCityAliasPaths().map((p) => (
                                <Route key={p} path={p} element={<CityAliasRedirect />} />
                            ))}
                            <Route path="/reparateur-electromenager-paris-urgent" element={<ParisUrgentPage />} />
                            {REPARATION_PARIS_PATHS.map((p) => (
                                <Route key={p} path={p} element={<ReparationParisPage />} />
                            ))}
                            <Route path="/comparatif-arrondissements-paris-electromenager-2026" element={<ParisComparePage />} />
                            <Route path="/reparateur-electromenager-hauts-de-seine" element={<HautsDeSeinePage />} />
                            <Route path="/reparateur-electromenager-yvelines" element={<YvelinesPage />} />
                            <Route path="/reparateur-electromenager-val-de-marne" element={<ValDeMarnePage />} />
                            <Route path="/admin/seo" element={<AdminSeoPage />} />
                            <Route path="/plan-du-site" element={<SitemapPage />} />
                            <Route path="/a-propos" element={<AboutPage />} />
                            <Route path="/notre-methodologie" element={<MethodologyPage />} />
                            {allCityHubPaths().map((p) => (
                                <Route key={p} path={p} element={<CityHubPage />} />
                            ))}
                            <Route path="/panne/:slug" element={<PannePage />} />
                            {allPanneGeoPaths().map((p) => (
                                <Route key={p} path={p} element={<PanneGeoPage />} />
                            ))}
                            <Route path="/zones-intervention" element={<ZonesPage />} />
                            <Route path="/tarifs" element={<TarifsPage />} />
                            <Route path="/faq" element={<FaqPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/blog" element={<BlogPage />} />
                            <Route path="/blog/:slug" element={<BlogArticle />} />
                            <Route path="/reparer-ou-racheter" element={<ReparerOuRacheterPage />} />
                            <Route path="/etudes/pannes-2026" element={<EtudePannes2026Page />} />
                            <Route path="/carte-interventions-idf" element={<CarteInterventionsPage />} />
                            <Route path="/codes-erreur" element={<ErrorCodeHubPage />} />
                            <Route path="/codes-erreur/:appliance" element={<ErrorCodeAppliancePage />} />
                            <Route path="/codes-erreur/:appliance/:code" element={<ErrorCodePage />} />
                            <Route path="/mentions-legales" element={<MentionsLegales />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Suspense>
                </Layout>
                <CookieConsent />
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
